import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Plus, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Field, FormError } from '@/components/form';
import { DetailList, ErrorState, PageHeader, Section } from '@/components/page';
import { ReasonDialog } from '@/components/reason-dialog';
import { auditDetail, describeAction } from '@/features/audit/describe';
import { CaseStatusBadge, EntryStatusBadge, EntryTypeBadge } from '@/features/register/badges';
import { CaseDocuments } from '@/features/register/case-documents';
import { DetailsEditor, filledDetails, newRow, type DetailRow } from '@/features/register/details-editor';
import { EntryActions } from '@/features/register/entry-actions';
import { api, errorMessage } from '@/lib/api';
import { can, useMe } from '@/lib/auth';
import { DETAIL_LABEL, formatDate, formatDateTime, PARTY_LABEL, SOURCE_LABEL } from '@/lib/labels';
import type { AuditEvent, Entry, FraudCase, Page, SkippedDetail } from '@/lib/types';

export const Route = createFileRoute('/_app/cases/$caseId')({ component: CasePage });

function CasePage() {
  const { caseId } = Route.useParams();
  const me = useMe();
  const queryClient = useQueryClient();
  const [reversing, setReversing] = useState(false);
  const [adding, setAdding] = useState(false);

  const query = useQuery({ queryKey: ['case', caseId], queryFn: () => api.get<FraudCase>(`/v1/cases/${caseId}`) });
  const history = useQuery({
    queryKey: ['case', caseId, 'history'],
    queryFn: () => api.get<Page<AuditEvent>>('/v1/audit-events', { caseId, pageSize: 100 }),
    enabled: can.readAudit(me),
  });

  const reverse = useMutation({
    mutationFn: (input: { reason: string; evidence?: string }) => api.post(`/v1/cases/${caseId}/reversal`, input),
    onSuccess: () => {
      toast.success('Case reversed; its entries no longer match');
      setReversing(false);
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      void queryClient.invalidateQueries({ queryKey: ['cases'] });
      void queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const c = query.data;
  const own = me.user.role !== 'SUPER_ADMIN' ? c.clientId === me.user.clientId : me.actingClient?.id === c.clientId;
  const active = c.status === 'ACTIVE';
  const canReverse = own && active && (c.source === 'APPLICATION' ? can.record(me) || can.admin(me) : can.admin(me));
  const canAdmin = own && can.admin(me);
  const title = c.source === 'APPLICATION' ? `Application ${c.sourceReference}` : c.source === 'MANUAL' ? 'Manual entry' : `Backfill${c.sourceReference ? ` · ${c.sourceReference}` : ''}`;

  return (
    <>
      <PageHeader
        back={
          <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Cases
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {title} <CaseStatusBadge status={c.status} />
          </span>
        }
        description={`Reported ${formatDate(c.reportedAt)}${c.reportedByName ? ` by ${c.reportedByName}` : ''}. Held until ${formatDate(c.expiresAt)}.`}
        actions={
          <>
            {canAdmin && active ? (
              <Button variant="outline" onClick={() => setAdding(true)}>
                <Plus /> Add details
              </Button>
            ) : null}
            {canReverse ? (
              <Button variant="outline" onClick={() => { reverse.reset(); setReversing(true); }}>
                <Undo2 /> Reverse
              </Button>
            ) : null}
          </>
        }
      />

      {!own && me.user.role === 'SUPER_ADMIN' ? (
        <p className="mb-4 rounded-lg border bg-muted px-4 py-2.5 text-[13px] text-muted-foreground">
          This case belongs to another client. Act as that client (top right) to change it.
        </p>
      ) : null}

      <div className="grid gap-5">
        <Section title="Summary">
          <DetailList
            items={[
              { label: 'Source', value: SOURCE_LABEL[c.source] },
              { label: 'Reference', value: c.sourceReference },
              { label: 'Entry type', value: <EntryTypeBadge type={c.entryType} /> },
              { label: 'Brand', value: c.brand },
              { label: 'Branch', value: c.branch },
              { label: 'Reported', value: formatDateTime(c.reportedAt) },
              ...(c.reason ? [{ label: 'Reason', value: c.reason }] : []),
              ...(c.evidence ? [{ label: 'Evidence', value: c.evidence }] : []),
              ...(c.reversedAt
                ? [
                    { label: 'Reversed', value: `${formatDateTime(c.reversedAt)}${c.reversedByName ? ` by ${c.reversedByName}` : ''}` },
                    { label: 'Reversal reason', value: c.reversalReason },
                  ]
                : []),
            ]}
          />
        </Section>

        <Section title="Entries" description="One per detail. Only active entries match lookups.">
          <EntriesTable entries={c.entries} actions={canAdmin} />
        </Section>

        <CaseDocuments
          caseId={c.id}
          documents={c.documents}
          canUpload={own && active && can.record(me)}
          canDownload={can.downloadDocuments(me)}
          canRemove={canAdmin}
        />

        {can.readAudit(me) ? (
          <Section title="History" description="Everything done to this case, newest first.">
            {history.isError ? (
              <div className="p-4">
                <ErrorState error={history.error} />
              </div>
            ) : (
              <ol className="divide-y">
                {(history.data?.items ?? []).map((e) => (
                  <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-5 py-2.5 text-sm">
                    <span className="w-40 shrink-0 text-xs text-muted-foreground tabular-nums">{formatDateTime(e.createdAt)}</span>
                    <span className="font-medium">{e.actorName ?? e.actorId}</span>
                    <span>{describeAction(e.action).toLowerCase()}</span>
                    {auditDetail(e) ? <span className="text-muted-foreground">· {auditDetail(e)}</span> : null}
                    {e.reason ? <span className="w-full pl-43 text-[13px] text-muted-foreground">“{e.reason}”</span> : null}
                  </li>
                ))}
              </ol>
            )}
          </Section>
        ) : null}
      </div>

      <ReasonDialog
        open={reversing}
        onOpenChange={setReversing}
        title="Reverse this case"
        description="Use this when the fraud finding was wrong. Every active entry on the case is ended so it stops matching. The case and its history stay on record."
        confirmLabel="Reverse case"
        destructive
        withEvidence
        pending={reverse.isPending}
        error={reverse.error}
        onConfirm={(input) => reverse.mutate(input)}
      />
      <AddDetailsDialog caseId={c.id} open={adding} onOpenChange={setAdding} />
    </>
  );
}

function EntriesTable({ entries, actions }: { entries: Entry[]; actions: boolean }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="px-5">Detail</TableHead>
          <TableHead>Stored as</TableHead>
          <TableHead>Entry type</TableHead>
          <TableHead>Status</TableHead>
          {actions ? <TableHead className="w-12"><span className="sr-only">Actions</span></TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((e) => (
          <TableRow key={e.id}>
            <TableCell className="px-5 py-2.5">
              <div className="text-sm">{e.value}</div>
              <div className="text-xs text-muted-foreground">
                {PARTY_LABEL[e.party]} · {DETAIL_LABEL[e.detailType]}
                {e.derived ? ' (derived)' : ''}
              </div>
            </TableCell>
            <TableCell className="font-mono text-[13px] text-muted-foreground">{e.normalizedValue}</TableCell>
            <TableCell>
              <EntryTypeBadge type={e.entryType} />
            </TableCell>
            <TableCell>
              <EntryStatusBadge status={e.status} />
              {e.endReason ? <div className="mt-1 max-w-[260px] text-xs text-muted-foreground">{e.endReason}</div> : null}
            </TableCell>
            {actions ? (
              <TableCell className="pr-3 text-right">
                <EntryActions entry={e} />
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AddDetailsDialog({ caseId, open, onOpenChange }: { caseId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<DetailRow[]>([newRow()]);
  const [reason, setReason] = useState('');
  const add = useMutation({
    mutationFn: () => api.post<{ added: Entry[]; skipped: SkippedDetail[] }>(`/v1/cases/${caseId}/entries`, { reason: reason.trim(), details: filledDetails(rows) }),
    onSuccess: (res) => {
      toast.success(`Added ${res.added.length} detail(s)`);
      if (res.skipped.length) toast.warning(res.skipped.map((s) => `${s.value}: ${s.reason}`).join('\n'));
      onOpenChange(false);
      setRows([newRow()]);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
      void queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>Add details to this case</DialogTitle>
            <DialogDescription>New details are held for six years from today.</DialogDescription>
          </DialogHeader>
          <FormError message={add.isError ? errorMessage(add.error) : null} />
          <DetailsEditor rows={rows} onChange={setRows} />
          <Field id="add-reason" label="Reason" hint="Recorded in the audit trail.">
            <Textarea id="add-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={add.isPending || reason.trim().length < 3 || filledDetails(rows).length === 0}>
              {add.isPending ? 'Saving…' : 'Add details'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

